import React, { useEffect, useRef, useState } from 'react';
import { Modal, Box, Typography, Button, useMediaQuery, Autocomplete, TextField, FormControl, IconButton, FormControlLabel, Switch } from '@mui/material';
import { useFormContext } from '../../contexts/CreateRecordFormContext';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Close } from '@mui/icons-material';
import { useThemePreference } from '../../contexts/ThemeContext';

const defaultGetOptionLabel = (option) => {
  if (typeof option === 'string') {
    return option;
  }

  if (!option) {
    return '';
  }

  if (typeof option.label === 'string') {
    return option.label;
  }

  if (typeof option.name === 'string') {
    return option.name;
  }

  if (typeof option.organizationName === 'string') {
    return option.organizationName;
  }

  return String(option);
};

const defaultGetOptionKey = (option) => {
  if (typeof option === 'string') {
    return option;
  }

  if (!option) {
    return '';
  }

  return option.value || option.id || option._id || defaultGetOptionLabel(option);
};

const defaultIsOptionEqualToValue = (option, value) => {
  if (option === value) {
    return true;
  }

  if (!option || !value) {
    return false;
  }

  if (option.value && value.value) {
    return option.value === value.value;
  }

  if (option.id && value.id) {
    return option.id === value.id;
  }

  if (option._id && value._id) {
    return option._id === value._id;
  }

  return defaultGetOptionLabel(option).toLowerCase() === defaultGetOptionLabel(value).toLowerCase();
};

const buildAutocompleteInputValues = (fields, initialValues) =>
  fields.reduce((accumulator, field) => {
    if (field.type !== 'autocomplete') {
      return accumulator;
    }

    const fieldValue = initialValues?.[field.name];

    if (!fieldValue || field.multiple) {
      return accumulator;
    }

    const getOptionLabel = field.getOptionLabel || defaultGetOptionLabel;

    return {
      ...accumulator,
      [field.name]: getOptionLabel(fieldValue),
    };
  }, {});

const RecordModal = ({ open, handleClose, fields, title, onSubmit, onDelete, isUpdate, allowMoveToDash, onMoveToDash, warning, initialValues = {} }) => {
  const { formState, updateField, resetForm, setFormValues } = useFormContext();
  const { isDarkMode } = useThemePreference();
  const wasOpenRef = useRef(false);
  const [autocompleteInputValues, setAutocompleteInputValues] = useState({});
  
  const isMobile = useMediaQuery('(max-width:600px)');
  const isLaptop = useMediaQuery('(max-width:1280px)')

  const handleChange = (e) => {
    updateField(e.target.name, e.target.value);
  };

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setFormValues(initialValues || {});
      setAutocompleteInputValues(buildAutocompleteInputValues(fields, initialValues));
    }
    wasOpenRef.current = open;
  }, [fields, initialValues, open, setFormValues]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit(formState); // Pass the form state when submitting
    resetForm();  // Reset the form state when the modal closes
    setAutocompleteInputValues({});
  };

  // Call resetForm when closing the modal
  const handleModalClose = () => {
    resetForm();  // Reset the form state when the modal closes
    setAutocompleteInputValues({});
    handleClose();
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '85vw' : isLaptop ? '60vw' : '35vw',
    maxHeight: 650,
    overflow: "hidden",
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
  };
  
  const headerFooterStyle = {
    position: 'sticky',
    zIndex: 1,
    backgroundColor: 'background.paper',
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <form onSubmit={handleFormSubmit} className="hive-record-modal-form">
      <Box sx={modalStyle} className="hive-record-modal">
        {/* Sticky Header */}
        <Box
          className="hive-record-modal-header"
          sx={{
            ...headerFooterStyle,
            top: 0,
            p: 2,
            borderBottom: '1px solid #ccc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography className="hive-record-modal-title" variant="h6" color={warning && "#d32f2f"}>{title}</Typography>
          <IconButton className="hive-record-modal-close" onClick={handleModalClose} size="small">
            <Close />
          </IconButton>
        </Box>
        {/* Scrollable Content */}
        <Box className="hive-record-modal-content" sx={{ overflowY: 'auto', flexGrow: 1, p: 2 }}>
        
          {fields.map((field) => (
            <div key={field.name} className="hive-record-modal-field">
              {field.type === 'autocomplete' ? (
                <Autocomplete
                  className="hive-record-modal-autocomplete"
                  sx={{marginTop: "1em", marginBottom: "1em"}}
                  options={field.options || []}
                  value={formState[field.name] ?? (field.multiple ? [] : null)}
                  onChange={(event, newValue) => updateField(field.name, newValue)}
                  {...(field.multiple ? {} : {
                    inputValue: autocompleteInputValues[field.name] ?? '',
                    onInputChange: (event, newInputValue) =>
                      setAutocompleteInputValues((prev) => ({
                        ...prev,
                        [field.name]: newInputValue,
                      })),
                    ListboxProps: {
                      key: `${field.name}-${autocompleteInputValues[field.name] ?? 'empty'}`
                    }
                  })}
                  getOptionLabel={field.getOptionLabel || defaultGetOptionLabel}
                  isOptionEqualToValue={field.isOptionEqualToValue || defaultIsOptionEqualToValue}
                  renderOption={(props, option) => {
                    const { key: optionKeyFromProps, ...optionProps } = props;
                    const getOptionKey = field.getOptionKey || defaultGetOptionKey;
                    const getOptionLabel = field.getOptionLabel || defaultGetOptionLabel;

                    return (
                      <li
                        {...optionProps}
                        key={`${String(getOptionKey(option))}-${String(getOptionLabel(option))}`}
                      >
                        {getOptionLabel(option)}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field.label}
                      helperText={field.helperText}
                      variant="outlined"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                    />
                  )}
                  multiple={field.multiple ? true : false}
                />
              ) : field.type === 'date' ? (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <FormControl fullWidth className="hive-record-modal-date-control">
                  <DatePicker
                      className="hive-record-modal-date-picker"
                      sx={{marginTop: "1em", marginBottom: "1em"}}
                      label={field.label}
                      value={formState[field.name] || ''}
                      onChange={(date) => updateField(field.name, date)}
                      slotProps={{
                        textField: {
                          helperText: field.helperText,
                        },
                      }}
                      renderInput={(params) => <TextField {...params} />}
                  />
                  </FormControl>
                </LocalizationProvider>                      
              ) : field.type === 'switch' ? (
                <FormControl fullWidth className="hive-record-modal-switch-control">
                  <FormControlLabel
                    sx={{ marginTop: "1em", marginBottom: "1em" }}
                    control={
                      <Switch
                        checked={Boolean(formState[field.name])}
                        onChange={(event) => updateField(field.name, event.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                </FormControl>
              ) : (
                <TextField
                  className="hive-record-modal-input"
                  required={field?.required && true}
                  type={field.type}
                  name={field.name}
                  label={field.label}
                  helperText={field.helperText}
                  value={formState[field.name] || ''}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  multiline={field?.multiline}
                  rows={6}
                />
              )}
            </div>
          ))}

        </Box>

        {/* Sticky Footer */}
        <Box className="hive-record-modal-footer" sx={{ ...headerFooterStyle, bottom: 0, p: 2, borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button className="hive-record-modal-button" type="submit" variant={isDarkMode ? "outlined" : "contained"} color="primary" sx={{ marginTop: "1em" }}>
            Submit
          </Button>
          {isUpdate && <Button className="hive-record-modal-button" variant={isDarkMode ? "outlined" : "contained"} color="error" sx={{marginTop: "1em"}} onClick={onDelete}>Delete</Button>}
          {allowMoveToDash && <Button className="hive-record-modal-button" variant={isDarkMode ? "outlined" : "contained"} color="warning" sx={{marginTop: "1em"}} onClick={onMoveToDash}>Add To Dashboard</Button>}
        </Box>
      </Box>
      </form>
    </Modal>
  );

};

export default RecordModal;
